import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { FuelSurchargeScheduleType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import {
  SmallButton,
  squareIconButtonSx,
} from '~components/Order/ordersDispatchStyledComponents';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import {
  FuelSurchargeSchedule,
  FuelSurchargeSchedulePrice,
} from '~hooks/useFuelSurchargeSchedule/model';
import { useFuelSurchargeSchedulePrices } from '~hooks/useFuelSurchargeSchedule/useFuelSurchargeSchedulePrices';
import { formatPercentage } from '~utils/utilFunctions';

const PAGE_LIMIT = 50;

interface FuelSurchargeSchedulePricesTableProps {
  fuelSurchargeSchedule: FuelSurchargeSchedule;
}

type FuelSurchargeSchedulePriceFieldErrors = {
  price?: string;
  billingSurcharge?: string;
};

type FuelSurchargeSchedulePriceEdit = {
  id: string;
  price: number;
  billingSurcharge: number;
};

// Everything a cell needs to render lives on the row. The column definitions
// only depend on the schedule's surcharge type, so typing into an edit field
// changes `rows` (a normal DataTable update) without rebuilding `columns` or
// remounting the grid — either of which throws the scroll position away.
type FuelSurchargeSchedulePriceRow = FuelSurchargeSchedulePriceEdit & {
  isEditing: boolean;
  isBusy: boolean;
  errors: FuelSurchargeSchedulePriceFieldErrors;
};

const NEW_ROW_ID = 'new';

export const FuelSurchargeSchedulePricesTable = observer(
  function FuelSurchargeSchedulePricesTable({
    fuelSurchargeSchedule,
  }: FuelSurchargeSchedulePricesTableProps) {
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingRow, setEditingRow] = useState<FuelSurchargeSchedulePriceEdit | null>(
      null,
    );
    const [fieldError, setFieldError] = useState<FuelSurchargeSchedulePriceFieldErrors>(
      {},
    );
    const fuelSurchargeScheduleId = fuelSurchargeSchedule.id;
    // a create that finishes after another schedule is selected must not add its row there
    const selectedScheduleIdRef = useRef(fuelSurchargeScheduleId);
    selectedScheduleIdRef.current = fuelSurchargeScheduleId;
    const surchargeType = fuelSurchargeSchedule.surchargeType;

    const {
      getFuelSurchargeSchedulePrices,
      createFuelSurchargeSchedulePrice,
      updateFuelSurchargeSchedulePrice,
      deleteFuelSurchargeSchedulePrice,
      isCreating,
      isUpdating,
      isDeleting,
    } = useFuelSurchargeSchedulePrices();
    const isBusy = isCreating || isUpdating || isDeleting;

    const {
      goToPage,
      isFetching,
      pagination,
      reloadCurrentPage,
      rows: prices,
      setRows: setPrices,
      startOver,
    } = useCursorPagination<FuelSurchargeSchedulePrice>({
      fetchPage: (params) =>
        getFuelSurchargeSchedulePrices({ fuelSurchargeScheduleId, ...params }),
      limit: PAGE_LIMIT,
      onFetchError: (error) => {
        console.error('Failed to load fuel charge schedule prices:', error);
      },
    });

    const resetEditingState = () => {
      setEditingId(null);
      setEditingRow(null);
      setFieldError({});
      setAdding(false);
    };

    // the parent keeps this table mounted while switching schedules
    useEffect(() => {
      resetEditingState();
      startOver();
    }, [fuelSurchargeScheduleId]);

    const rows = useMemo<FuelSurchargeSchedulePriceRow[]>(() => {
      const toRow = (
        price: FuelSurchargeSchedulePriceEdit,
        isEditing: boolean,
      ): FuelSurchargeSchedulePriceRow => ({
        id: price.id,
        price: price.price,
        billingSurcharge: price.billingSurcharge,
        isEditing,
        isBusy,
        errors: isEditing ? fieldError : {},
      });

      const baseRows = prices.map((price) =>
        editingId === price.id && editingRow
          ? toRow(editingRow, true)
          : toRow(price, false),
      );

      if (editingId === NEW_ROW_ID && editingRow) {
        return [toRow(editingRow, true), ...baseRows];
      }

      return baseRows;
    }, [prices, editingId, editingRow, fieldError, isBusy]);

    const handleEdit = (id: string) => {
      setEditingId(id);
      if (id === NEW_ROW_ID) {
        setEditingRow({ id: NEW_ROW_ID, price: 0, billingSurcharge: 0 });
      } else {
        const row = prices.find((r) => r.id === id);
        if (row)
          setEditingRow({
            id: row.id,
            price: row.price,
            billingSurcharge: row.billingSurcharge,
          });
      }
    };

    const handleSave = async (id: string) => {
      const row = editingRow;
      if (!row || !fuelSurchargeScheduleId) return;

      const errors: FuelSurchargeSchedulePriceFieldErrors = {};
      if (row.price === undefined || row.price === null)
        errors.price = 'Price is required';
      if (row.billingSurcharge === undefined || row.billingSurcharge === null)
        errors.billingSurcharge = 'Billing surcharge is required';
      if (Object.keys(errors).length > 0) {
        setFieldError(errors);
        return;
      }
      setFieldError({});

      try {
        if (id === NEW_ROW_ID) {
          const newPrice = await createFuelSurchargeSchedulePrice({
            fuelSurchargeScheduleId,
            price: Number(row.price),
            billingSurcharge: Number(row.billingSurcharge),
          });
          if (selectedScheduleIdRef.current === fuelSurchargeScheduleId) {
            setPrices((prev) => [newPrice, ...prev]);
          }
          setAdding(false);
        } else {
          const updatedPrice = await updateFuelSurchargeSchedulePrice({
            id,
            price: Number(row.price),
            billingSurcharge: Number(row.billingSurcharge),
          });
          setPrices((prev) =>
            prev.map((price) => (price.id === id ? updatedPrice : price)),
          );
        }
        setEditingId(null);
        setEditingRow(null);
      } catch (error) {
        console.error('Failed to save fuel charge schedule price:', error);
        setEditingId(null);
        setEditingRow(null);
        setAdding(false);
      }
    };

    const handleCancel = (id: string) => {
      if (id === NEW_ROW_ID) {
        setAdding(false);
      }
      setEditingId(null);
      setEditingRow(null);
      setFieldError({});
    };

    const handleDelete = async (id: string) => {
      if (id === NEW_ROW_ID) {
        setAdding(false);
        setEditingRow(null);
        return;
      }
      try {
        await deleteFuelSurchargeSchedulePrice(id);
        await reloadCurrentPage();
      } catch (error) {
        console.error('Failed to delete fuel charge schedule price:', error);
        setAdding(false);
      }
    };

    // The column definitions below are memoized on surcharge type only, so
    // they reach the handlers through a ref rather than closing over them.
    const handlersRef = useRef({ handleEdit, handleSave, handleCancel, handleDelete });
    handlersRef.current = { handleEdit, handleSave, handleCancel, handleDelete };

    const handleAddClick = () => {
      setAdding(true);
      setEditingId(NEW_ROW_ID);
      setEditingRow({ id: NEW_ROW_ID, price: 0, billingSurcharge: 0 });
    };

    const columns = useMemo<Array<DataTableColumn<FuelSurchargeSchedulePriceRow>>>(() => {
      const isCurrency = surchargeType !== FuelSurchargeScheduleType.PERCENTAGE;

      const formatSurchargeValue = (value: number) => {
        if (isCurrency) {
          return `$${Number(value).toFixed(2)}`;
        }
        return formatPercentage(value);
      };

      const surchargeInputAdornment = isCurrency
        ? { startAdornment: '$' }
        : { endAdornment: '%' };

      return [
        {
          field: 'price',
          headerName: t('administration.fuel_surcharge_schedule.price'),
          minWidth: 150,
          renderCell: (row) => {
            if (row.isEditing) {
              return (
                <TextField
                  key={`edit-${row.id}-price`}
                  type="number"
                  value={row.price ?? ''}
                  onChange={(e) => {
                    setEditingRow((prev) =>
                      prev ? { ...prev, price: Number(e.target.value) } : prev,
                    );
                    setFieldError((prev) => ({ ...prev, price: undefined }));
                  }}
                  size="small"
                  fullWidth
                  slotProps={{
                    htmlInput: { step: '0.01' },
                    input: { startAdornment: '$' },
                  }}
                  disabled={row.isBusy}
                  error={!!row.errors.price}
                />
              );
            }
            return `$${Number(row.price).toFixed(2)}`;
          },
        },
        {
          field: 'billingSurcharge',
          headerName: t('administration.fuel_surcharge_schedule.billing_surcharge'),
          minWidth: 200,
          renderCell: (row) => {
            if (row.isEditing) {
              return (
                <TextField
                  key={`edit-${row.id}-surcharge`}
                  type="number"
                  value={row.billingSurcharge ?? ''}
                  onChange={(e) => {
                    setEditingRow((prev) =>
                      prev ? { ...prev, billingSurcharge: Number(e.target.value) } : prev,
                    );
                    setFieldError((prev) => ({
                      ...prev,
                      billingSurcharge: undefined,
                    }));
                  }}
                  size="small"
                  fullWidth
                  slotProps={{
                    htmlInput: { step: isCurrency ? '0.01' : '0.0001' },
                    input: surchargeInputAdornment,
                  }}
                  disabled={row.isBusy}
                  error={!!row.errors.billingSurcharge}
                />
              );
            }
            return formatSurchargeValue(row.billingSurcharge);
          },
        },
        actionsColumn((row) => {
          if (row.isEditing) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmallButton
                  aria-label={t('actions.save')}
                  color="brandV2Yellow"
                  variant="contained"
                  onClick={() => handlersRef.current.handleSave(row.id)}
                  disabled={row.isBusy}
                  sx={squareIconButtonSx}
                >
                  <SaveIcon sx={{ fontSize: 18 }} />
                </SmallButton>
                <SmallButton
                  aria-label={t('actions.cancel')}
                  color="secondary"
                  variant="outlined"
                  onClick={() => handlersRef.current.handleCancel(row.id)}
                  disabled={row.isBusy}
                  sx={squareIconButtonSx}
                >
                  <CancelIcon sx={{ fontSize: 18 }} />
                </SmallButton>
              </Box>
            );
          }
          return (
            <>
              <IconButton
                aria-label={t('actions.edit')}
                disabled={row.isBusy}
                onClick={() => handlersRef.current.handleEdit(row.id)}
                size="small"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                aria-label={t('actions.delete')}
                disabled={row.isBusy}
                onClick={() => handlersRef.current.handleDelete(row.id)}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </>
          );
        }),
      ];
    }, [surchargeType]);

    return (
      <Box>
        <Box
          sx={{
            display: 'flex' as const,
            alignItems: 'center',
            justifyContent: 'flex-end',
            mb: 2,
          }}
        >
          <Box sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }}>
            {t('administration.fuel_surcharge_schedule.index')}:
            <Box
              component="span"
              sx={{ fontWeight: 700, color: 'text.primary', display: 'inline', ml: 0.5 }}
            >
              {fuelSurchargeSchedule.fuelPriceIndex.name}
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ ml: 1 }}>
            <DataGridPager
              isFetching={isFetching}
              onNavigate={(link) => {
                resetEditingState();
                void goToPage(link);
              }}
              pagination={pagination}
            />
          </Box>
          <SmallButton
            color="brandV2Yellow"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={!fuelSurchargeScheduleId || isBusy || adding}
            sx={{ ml: 1 }}
          >
            {t('administration.fuel_surcharge_schedule.add_price')}
          </SmallButton>
        </Box>
        <DataTable
          // Keyed on the schedule only: selecting a different schedule starts
          // a fresh table (scrolled to the top), but editing a row must not.
          key={`fuel-surcharge-schedule-prices-${fuelSurchargeScheduleId}-${surchargeType}`}
          id="fuel-surcharge-schedule-prices"
          columns={columns}
          rows={rows}
          loading={isFetching || isBusy}
          hideToolbar
          sx={{
            // 316px = the panel's 226px offset + its 24px padding + the 48px Add Price
            // row and its margin + the table's 16px bottom padding and 2px border, so
            // the table scrolls inside the panel.
            maxHeight: 'calc(100vh - 316px - var(--tread-agent-dock-height, 0px))',
            overflowY: 'auto',
          }}
        />
      </Box>
    );
  },
);
